import React, { useState, useEffect } from 'react';
import { Box, IconButton, Popover, Grid, useTheme, useMediaQuery } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import Text from 'components/mui/Text';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Button from 'components/mui/Button';
import { typography } from 'theme';
import { alpha } from '@mui/material/styles';
import PropTypes from 'prop-types';

const DateRangePicker = ({
  value,
  onChange,
  label = "Select Date Range",
  startIcon,
  selectSx = {},
  onRangeChange,
  displayValueFormatter
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'year'
  const [yearRangeStart, setYearRangeStart] = useState(() => {
    const currentYear = new Date().getFullYear();
    return Math.floor(currentYear / 10) * 10; // Start of decade
  });

  // Range mode state
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [committedStartDate, setCommittedStartDate] = useState(null);
  const [committedEndDate, setCommittedEndDate] = useState(null);

  const open = Boolean(anchorEl);

  // Parse range value on mount or when value changes
  // Parse range value on mount or when value changes
  useEffect(() => {
    if (value) {
      const parsed = parseRangeValue(value);
      if (parsed) {
        setCommittedStartDate(parsed.startDate);
        setCommittedEndDate(parsed.endDate);
        setTempStartDate(parsed.startDate);
        setTempEndDate(parsed.endDate);
      }
    } else {
      setCommittedStartDate(null);
      setCommittedEndDate(null);
      setTempStartDate(null);
      setTempEndDate(null);
    }
  }, [value]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    // Initialize temp dates from committed dates when opening
    setTempStartDate(committedStartDate);
    setTempEndDate(committedEndDate);
  };

  const handleClose = () => {
    setAnchorEl(null);
    // Reset temp dates on close without applying
    setTempStartDate(committedStartDate);
    setTempEndDate(committedEndDate);
    setViewMode('month'); // Reset to month view when closing
  };

  const parseRangeValue = (val) => {
    // Parse format: "01 Sep 25 - 26 Sep 25" or "DD MMM YY - DD MMM YY"
    const match = val.match(/(\d+)\s+(\w+)\s+(\d+)\s*-\s*(\d+)\s+(\w+)\s+(\d+)/i);
    if (match) {
      const startDay = parseInt(match[1]);
      const startMonth = parseMonth(match[2]);
      const startYear = parseInt(match[3]) + 2000; // Convert YY to YYYY

      const endDay = parseInt(match[4]);
      const endMonth = parseMonth(match[5]);
      const endYear = parseInt(match[6]) + 2000; // Convert YY to YYYY

      return {
        startDate: new Date(startYear, startMonth, startDay),
        endDate: new Date(endYear, endMonth, endDay)
      };
    }
    // Fallback to old format for backward compatibility
    const oldMatch = val.match(/from\s+(\d+)\s+(\w+)(?:\s+(\d+))?\s+to\s+(\d+)\s+(\w+)(?:\s+(\d+))?/i);
    if (oldMatch) {
      const startDay = parseInt(oldMatch[1]);
      const startMonth = parseMonth(oldMatch[2]);
      const startYear = oldMatch[3] ? parseInt(oldMatch[3]) : new Date().getFullYear();

      const endDay = parseInt(oldMatch[4]);
      const endMonth = parseMonth(oldMatch[5]);
      const endYear = oldMatch[6] ? parseInt(oldMatch[6]) : new Date().getFullYear();

      return {
        startDate: new Date(startYear, startMonth, startDay),
        endDate: new Date(endYear, endMonth, endDay)
      };
    }
    return null;
  };

  const parseMonth = (monthStr) => {
    const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    const index = months.findIndex(m => m === monthStr.toLowerCase().substring(0, 3));
    return index !== -1 ? index : new Date().getMonth();
  };

  const formatDate = (date) => {
    if (!date) return '';
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear().toString().slice(-2); // Last 2 digits of year
    return `${day.toString().padStart(2, '0')} ${month} ${year}`;
  };

  const formatRangeValue = (startDate, endDate) => {
    if (!startDate || !endDate) return '';
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const handleDateSelect = (day) => {
    const clickedDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);

    // Range mode
    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      // Start new range
      setTempStartDate(clickedDate);
      setTempEndDate(null);
    } else {
      // Set end date
      if (clickedDate < tempStartDate) {
        // Swap if end < start
        setTempEndDate(tempStartDate);
        setTempStartDate(clickedDate);
      } else {
        setTempEndDate(clickedDate);
      }
    }
  };

  const handleClear = () => {
    // If there's a committed range, clear it
    if (committedStartDate && committedEndDate) {
      setCommittedStartDate(null);
      setCommittedEndDate(null);
      setTempStartDate(null);
      setTempEndDate(null);

      // Clear the value in parent component
      if (onRangeChange) {
        onRangeChange({ startDate: null, endDate: null });
      }

      if (onChange) {
        onChange({ target: { value: '' } });
      }
    } else {
      // If no committed range, just reset temp dates
      setTempStartDate(null);
      setTempEndDate(null);
    }
  };

  const handleApply = () => {
    if (tempStartDate && tempEndDate) {
      setCommittedStartDate(tempStartDate);
      setCommittedEndDate(tempEndDate);
      const formattedValue = formatRangeValue(tempStartDate, tempEndDate);

      if (onRangeChange) {
        onRangeChange({ startDate: tempStartDate, endDate: tempEndDate });
      }

      if (onChange) {
        onChange({ target: { value: formattedValue } });
      }

      handleClose();
    }
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (direction === 'prev') {
        newDate.setMonth(prev.getMonth() - 1);
      } else {
        newDate.setMonth(prev.getMonth() + 1);
      }
      return newDate;
    });
  };

  const navigateYear = (direction) => {
    if (direction === 'prev') {
      setYearRangeStart(prev => prev - 10);
    } else {
      setYearRangeStart(prev => prev + 10);
    }
  };

  const handleYearSelect = (year) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setFullYear(year);
      return newDate;
    });
    setViewMode('month');
  };

  const handleHeaderClick = () => {
    setViewMode(viewMode === 'month' ? 'year' : 'month');
  };

  const getYearRange = () => {
    const years = [];
    for (let i = 0; i < 12; i++) {
      years.push(yearRangeStart + i);
    }
    return years;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    // Add previous month's trailing days
    const prevMonth = new Date(year, month - 1, 0);
    const prevMonthDays = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        day: prevMonthDays - i,
        isCurrentMonth: false,
        isPreviousMonth: true
      });
    }
    // Add current month's days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        day: day,
        isCurrentMonth: true,
        isPreviousMonth: false
      });
    }
    // Add next month's leading days to complete the week
    const currentLength = days.length;
    const remainingCells = currentLength % 7 === 0 ? 0 : 7 - (currentLength % 7);
    for (let day = 1; day <= remainingCells; day++) {
      days.push({
        day: day,
        isCurrentMonth: false,
        isPreviousMonth: false
      });
    }
    return days;
  };

  const getMonthName = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const isDateInRange = (date, startDate, endDate) => {
    if (!startDate || !endDate) return false;
    return date >= startDate && date <= endDate;
  };

  const isDateEqual = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate();
  };

  const getDisplayValue = () => {
    let baseValue = label || 'Select Date Range';

    if (committedStartDate && committedEndDate) {
      baseValue = formatRangeValue(committedStartDate, committedEndDate);
    } else if (value) {
      baseValue = value;
    }

    if (typeof displayValueFormatter === 'function') {
      return displayValueFormatter({
        baseValue,
        startDate: committedStartDate,
        endDate: committedEndDate,
        hasRange: Boolean(committedStartDate && committedEndDate)
      }) ?? baseValue;
    }

    return baseValue;
  };

  const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const monthDays = getDaysInMonth(currentDate);
  const displayValue = getDisplayValue();

  return (
    <Box sx={{ minWidth: 60 }}>
      <Box
        onClick={handleClick}
        sx={{
          backgroundColor: theme.palette.custom?.background?.helper || '#253246',
          color: theme.palette.custom?.text?.heading,
          borderRadius: theme.spacing(1.25),
          px: theme.spacing(2),
          py: theme.spacing(1),
          minHeight: theme.spacing(4.5),
          boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.custom?.text?.subheading || '#94a3b8', 0.25)}`,
          display: 'flex',
          alignItems: 'center',
          gap: theme.spacing(1.5),
          cursor: 'pointer',
          fontSize: { xs: typography.fontSize.helperText, md: typography.fontSize.label },
          fontWeight: typography.fontWeight.medium,
          minWidth: 140,
          //   '&:hover': { 
          //     backgroundColor: theme.palette.custom?.background?.layer2 || '#2d3748' 
          //   },
          ...selectSx,
        }}
      >
        {startIcon && (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.custom?.text?.subheading || '#94a3b8',
            flexShrink: 0,
            margin: displayValue ? '0' : 'auto',
            '& svg': {
              fontSize: { xs: '20px', md: '18px' },
              display: 'block',
            },
          }}>
            {startIcon}
          </Box>
        )}
        {displayValue && (
          <Box
            component="span"
            sx={{
              flex: 1,
              fontSize: { xs: typography.fontSize.helperText, md: typography.fontSize.label },
              display: 'block',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {displayValue}
          </Box>
        )}
        <Box sx={{
          display: 'none',
          color: theme.palette.custom?.text?.subheading || '#94a3b8',
          fontSize: { xs: typography.fontSize.helperText, md: typography.fontSize.label }
        }}>▲</Box>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        sx={{
          '& .MuiPopover-paper': {
            backgroundColor: theme.palette.custom?.background?.helper || '#1e2a3a',
            color: theme.palette.custom?.text?.heading,
            borderRadius: theme.spacing(1.5),
            minWidth: { xs: 250, md: 300 },
            maxWidth: { xs: 270, md: 320 },
            overflow: 'hidden',
          }
        }}
      >
        <Box sx={{ p: { xs: theme.spacing(1), md: theme.spacing(2) } }}>
          {/* Header with month/year navigation */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: { xs: theme.spacing(1.5), md: theme.spacing(2) },
            px: { xs: theme.spacing(0.5), md: theme.spacing(1) }
          }}>
            <IconButton
              onClick={() => viewMode === 'month' ? navigateMonth('prev') : navigateYear('prev')}
              sx={{
                color: theme.palette.custom?.text?.heading || '#FFFFFF',
                p: { xs: theme.spacing(0.25), md: theme.spacing(0.5) },
                '&:hover': { backgroundColor: alpha(theme.palette.custom?.text?.heading || '#FFFFFF', 0.1) },
                '& svg': {
                  fontSize: { xs: '18px', md: '24px' },
                },
              }}
            >
              <ChevronLeft />
            </IconButton>
            <Text
              onClick={handleHeaderClick}
              sx={{
                fontSize: { xs: typography.fontSize.helperText, md: typography.fontSize.paragraph },
                fontWeight: typography.fontWeight.semibold,
                color: theme.palette.custom?.text?.heading || '#FFFFFF',
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8
                }
              }}
            >
              {viewMode === 'month' ? getMonthName(currentDate) : `${yearRangeStart} - ${yearRangeStart + 11}`}
            </Text>
            <IconButton
              onClick={() => viewMode === 'month' ? navigateMonth('next') : navigateYear('next')}
              sx={{
                color: theme.palette.custom?.text?.heading || '#FFFFFF',
                p: { xs: theme.spacing(0.25), md: theme.spacing(0.5) },
                '&:hover': { backgroundColor: alpha(theme.palette.custom?.text?.heading || '#FFFFFF', 0.1) },
                '& svg': {
                  fontSize: { xs: '18px', md: '24px' },
                },
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>

          {viewMode === 'month' ? (
            <>
              {/* Day labels */}
              <Grid container spacing={0} sx={{ mb: { xs: theme.spacing(0.75), md: theme.spacing(1) } }}>
                {days.map((day, index) => (
                  <Grid item xs={12 / 7} key={index}>
                    <Box sx={{
                      textAlign: 'center',
                      py: { xs: theme.spacing(0.5), md: theme.spacing(0.8) },
                      fontSize: { xs: typography.fontSize.helperText, md: '10px' },
                      fontWeight: typography.fontWeight.medium,
                      color: theme.palette.custom?.text?.subheading || '#94a3b8',
                      textTransform: 'uppercase'
                    }}>
                      {day}
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Calendar grid */}
              <Grid container spacing={0}>
                {monthDays.map((dayObj, index) => {
                  const { day, isCurrentMonth, isPreviousMonth } = dayObj;

                  // Create correct date based on which month the day belongs to
                  let cellDate;
                  if (isPreviousMonth) {
                    const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, day);
                    cellDate = prevMonth;
                  } else if (isCurrentMonth) {
                    cellDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                  } else {
                    const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, day);
                    cellDate = nextMonth;
                  }

                  let isStartDate = false;
                  let isEndDate = false;
                  let isInRange = false;

                  // Range mode - only highlight dates in current month
                  const startDate = tempStartDate;
                  const endDate = tempEndDate;

                  if (startDate && endDate && isCurrentMonth) {
                    isInRange = isDateInRange(cellDate, startDate, endDate);
                    isStartDate = isDateEqual(cellDate, startDate);
                    isEndDate = isDateEqual(cellDate, endDate);
                  } else if (startDate && isCurrentMonth) {
                    isStartDate = isDateEqual(cellDate, startDate);
                  }

                  const isSelected = isStartDate || isEndDate;

                  return (
                    <Grid item xs={12 / 7} key={index}>
                      <Box
                        onClick={() => day && isCurrentMonth && handleDateSelect(day)}
                        sx={{
                          textAlign: 'center',
                          py: { xs: theme.spacing(0.5), md: theme.spacing(1) },
                          cursor: (day && isCurrentMonth) ? 'pointer' : 'default',
                          fontSize: { xs: typography.fontSize.helperText, md: '12px' },
                          fontWeight: typography.fontWeight.medium,
                          color: isCurrentMonth
                            ? (isSelected
                              ? theme.palette.custom?.text?.buttonOnPrimary || '#FFFFFF'
                              : theme.palette.custom?.text?.heading || '#FFFFFF')
                            : theme.palette.custom?.text?.subheading || '#64748b',
                          backgroundColor: isSelected
                            ? (theme.palette.custom?.button?.primary || '#0ea5e9')
                            : (isInRange && !isSelected
                              ? alpha(theme.palette.custom?.button?.primary || '#0ea5e9', 0.2)
                              : 'transparent'),
                          borderRadius: isInRange && !isSelected ? 0 : '50%',
                          minHeight: { xs: 24, md: 28 },
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          //   mx: { xs: theme.spacing(0.1), md: theme.spacing(0.2) },
                          position: 'relative',
                          '&:hover': (day && isCurrentMonth) ? {
                            backgroundColor: isSelected
                              ? (theme.palette.custom?.button?.primary || '#0ea5e9')
                              : alpha(theme.palette.custom?.button?.primary || '#0ea5e9', 0.2),
                          } : {},
                          // Range mode styling
                          ...(isStartDate && {
                            borderTopLeftRadius: '30%',
                            borderBottomLeftRadius: '30%',
                            borderTopRightRadius: '0%',
                            borderBottomRightRadius: '0%',
                          }),
                          ...(isEndDate && {
                            borderTopLeftRadius: '0%',
                            borderBottomLeftRadius: '0%',
                            borderTopRightRadius: '30%',
                            borderBottomRightRadius: '30%',
                          }),
                        }}
                      >
                        {day}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          ) : (
            <>
              {/* Year grid */}
              <Grid container spacing={0}>
                {getYearRange().map((year, index) => {
                  const isSelected = currentDate.getFullYear() === year;

                  return (
                    <Grid item xs={4} key={index}>
                      <Box
                        onClick={() => handleYearSelect(year)}
                        sx={{
                          textAlign: 'center',
                          py: { xs: theme.spacing(1), md: theme.spacing(1.5) },
                          cursor: 'pointer',
                          fontSize: { xs: typography.fontSize.helperText, md: typography.fontSize.label },
                          fontWeight: typography.fontWeight.medium,
                          color: isSelected
                            ? theme.palette.custom?.text?.buttonOnPrimary || '#FFFFFF'
                            : theme.palette.custom?.text?.heading || '#FFFFFF',
                          backgroundColor: isSelected
                            ? (theme.palette.custom?.button?.primary || '#0ea5e9')
                            : 'transparent',
                          borderRadius: theme.spacing(1),
                          mx: { xs: theme.spacing(0.25), md: theme.spacing(0.5) },
                          mb: { xs: theme.spacing(0.5), md: theme.spacing(1) },
                          '&:hover': {
                            backgroundColor: isSelected
                              ? (theme.palette.custom?.button?.primary || '#0ea5e9')
                              : alpha(theme.palette.custom?.button?.primary || '#0ea5e9', 0.2),
                          },
                        }}
                      >
                        {year}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          )}

          {/* Footer buttons - Clear and Apply */}
          <Box sx={{
            mt: { xs: theme.spacing(1.5), md: theme.spacing(2) },
            display: 'flex',
            gap: theme.spacing(1.5),
            justifyContent: 'center'
          }}>
            <Button
              onClick={handleClear}
              sx={{
                backgroundColor: 'transparent',
                color: theme.palette.custom?.text?.heading || '#FFFFFF',
                border: `1px solid ${theme.palette.custom?.stroke?.divider || '#334155'}`,
                padding: { xs: `${theme.spacing(0.75)} ${theme.spacing(1.5)}`, md: `${theme.spacing(1)} ${theme.spacing(2.5)}` },
                fontSize: { xs: typography.fontSize.helperText, md: '12px' },
                fontWeight: typography.fontWeight.semibold,
                borderColor: 'white',
                borderRadius: theme.spacing(0.5),
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.custom?.text?.heading || '#FFFFFF', 0.1),
                  borderColor: 'white',
                },
                flex: 1,
                boxShadow: 'none',
              }}
            >
              Clear
            </Button>
            <Button
              onClick={handleApply}
              disabled={!tempStartDate || !tempEndDate}
              sx={{
                backgroundColor: theme.palette.custom?.button?.primary || '#0ea5e9',
                color: theme.palette.custom?.text?.buttonOnPrimary || '#FFFFFF',
                padding: { xs: `${theme.spacing(0.75)} ${theme.spacing(1.5)}`, md: `${theme.spacing(1)} ${theme.spacing(2.5)}` },
                fontSize: { xs: typography.fontSize.helperText, md: '12px' },
                fontWeight: typography.fontWeight.semibold,
                borderRadius: theme.spacing(1),
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: theme.palette.custom?.button?.primary || '#0284c7',
                  opacity: 0.9,
                },
                '&:disabled': {
                  backgroundColor: alpha(theme.palette.custom?.button?.primary || '#0ea5e9', 0.5),
                  color: alpha(theme.palette.custom?.text?.buttonOnPrimary || '#FFFFFF', 0.5),
                },
                flex: 1,
                boxShadow: 'none',
              }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default DateRangePicker;

DateRangePicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  label: PropTypes.string,
  startIcon: PropTypes.node,
  selectSx: PropTypes.object,
  onRangeChange: PropTypes.func,
  displayValueFormatter: PropTypes.func,
};

